/**
 * Set (or release) a case's owning analyst via POST /cases/{family}/claim.
 *
 * The owner is also the CX lead of each Backoffice project the case has, so a
 * claim is pushed there too. The claim is kept whatever Backoffice says; if
 * some projects could not be updated the person is told they are behind. A
 * refused change (a case with Backoffice projects cannot be released, only
 * reassigned) is shown as the server's own message and resolves to `null`.
 */

import { useCallback } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { notifyBackofficeProjectsBehind } from '@/utils/backofficeSyncNotice';

type ClaimResult = Awaited<ReturnType<typeof vnextApiClient.claimCase>>;

export interface UseClaimCaseResult {
  claim: (familyKey: string, userId: string | null) => Promise<ClaimResult | null>;
}

export function useClaimCase(): UseClaimCaseResult {
  const claim = useCallback(async (familyKey: string, userId: string | null) => {
    try {
      const result = await vnextApiClient.claimCase(familyKey, userId);
      notifyBackofficeProjectsBehind(result.backofficeProjectsFailed);
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      notifications.show({
        title: 'Could not change the case owner',
        message: e.message,
        color: 'red',
      });
      return null;
    }
  }, []);

  return { claim };
}
